import React from 'react';
import { SchematicShell, Rect, Line } from './SchematicPrimitives';
import { bedProportions } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for `bed` — the Track-Arm platform bed.
 *
 * Matches `bedRenderer` in furnitureRenderers.js (shared proportions live in
 * `proceduralRenderers/proceduralSpecs.js` → `bedProportions`): a low crisp
 * upholstered platform on thin matte-black metal pin legs, a mattress sunk
 * slightly into the platform, two crisp pillows + a flat duvet, and a thin
 * squared low headboard at the head end (-z).
 *
 * Front (long edge facing viewer): low platform band + mattress slab + pillows
 * + duvet, with the headboard rising behind the mattress.
 * Side (short end): headboard at the head end (left), platform + mattress +
 * pillow between, pin legs near + far. Right view mirrors left.
 *
 * All numbers come from `bedProportions` (floor-relative, floor at the bottom of
 * the viewport), so the 2D elevation can never drift from the 3D geometry.
 */

export function BedSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const b = bedProportions({ width, height, depth });

  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        drawFrame={false}
      >
        <g
          transform={
            view === 'right' ? `scale(-1, 1) translate(${-depth}, 0)` : ''
          }
        >
          <SideProfile
            depth={depth}
            height={height}
            b={b}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT — svg y grows downward; floor is at y = height.
  const yTop = (fromFloor) => height - fromFloor;
  const legCx = [b.legInset, width - b.legInset];
  const legW = 2 * b.legR;
  const mattressW = width - 0.05;
  const pillowTop = b.mattressTop + b.pillowH - b.pillowSink;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Thin squared low headboard rising behind the mattress. */}
      <Rect
        x={0}
        y={yTop(b.platformTop + b.hbH)}
        width={width}
        height={b.hbH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Mattress slab (slightly inset from the platform). */}
      <Rect
        x={(width - mattressW) / 2}
        y={yTop(b.mattressTop)}
        width={mattressW}
        height={b.mattressH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Low upholstered platform. */}
      <Rect
        x={0}
        y={yTop(b.platformTop)}
        width={width}
        height={b.platformH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Duvet — thin line across the lower mattress. */}
      <Line
        x1={0}
        y1={yTop(b.mattressTop + 0.02 + b.duvetH / 2)}
        x2={width}
        y2={yTop(b.mattressTop + 0.02 + b.duvetH / 2)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Two pillows resting on the mattress near the head. */}
      <Rect
        x={width / 2 - b.pillowW * 0.52 - b.pillowW / 2}
        y={yTop(pillowTop)}
        width={b.pillowW}
        height={b.pillowH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={width / 2 + b.pillowW * 0.52 - b.pillowW / 2}
        y={yTop(pillowTop)}
        width={b.pillowW}
        height={b.pillowH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Two front pin legs. */}
      {legCx.map((cx, i) => (
        <Rect
          key={`leg-${i}`}
          x={cx - b.legR}
          y={yTop(b.legH)}
          width={legW}
          height={b.legH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </SchematicShell>
  );
}

function SideProfile({ depth, height, b, strokeColor, strokeWidth }) {
  // Side view: viewport width = depth. Renderer z maps to x via z + depth/2, so
  // the head of the bed (-z, headboard) is on the left, the foot (+z) on the
  // right.
  const yTop = (fromFloor) => height - fromFloor;
  const hbX = depth / 2 + b.hbZ - b.hbThick / 2;
  const mattressX = 0.01; // headboard front face sits at z = -depth/2 + 0.01
  const legCx = [b.legInset, depth - b.legInset];
  const legW = 2 * b.legR;
  const pillowX = depth / 2 + b.pillowZ - b.pillowD / 2;
  const pillowTop = b.mattressTop + b.pillowH - b.pillowSink;

  return (
    <g>
      {/* Headboard at the head end (left). */}
      <Rect
        x={hbX}
        y={yTop(b.platformTop + b.hbH)}
        width={b.hbThick}
        height={b.hbH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Mattress spanning from the headboard front to the foot. */}
      <Rect
        x={mattressX}
        y={yTop(b.mattressTop)}
        width={depth - mattressX}
        height={b.mattressH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Low upholstered platform spanning the full depth. */}
      <Rect
        x={0}
        y={yTop(b.platformTop)}
        width={depth}
        height={b.platformH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Duvet / sheet line across the mattress. */}
      <Line
        x1={mattressX}
        y1={yTop(b.mattressTop + 0.02 + b.duvetH / 2)}
        x2={depth}
        y2={yTop(b.mattressTop + 0.02 + b.duvetH / 2)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Pillow lump near the headboard. */}
      <Rect
        x={pillowX}
        y={yTop(pillowTop)}
        width={b.pillowD}
        height={b.pillowH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Pin legs near + far. */}
      {legCx.map((cx, i) => (
        <Rect
          key={`leg-${i}`}
          x={cx - b.legR}
          y={yTop(b.legH)}
          width={legW}
          height={b.legH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </g>
  );
}

BedSchematic.supportsSideViews = true;
